import type { AdminMetricsDto, AdminMetricsWindow } from '@carcheck/shared';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from './client';

/**
 * TanStack Query hook for the admin dashboard analytics rollup
 * (`GET /admin/metrics?window=<w>`, AUT — dashboard analytics). Feeds the
 * four analytics dashboard tiles (GMV series, order-structure donut,
 * online-now, orders-by-hour), all from ONE fetch per window.
 *
 * Query-key convention (ADMIN-10 guardrail / queryClient.ts):
 *   `['admin', 'metrics', <window>]` — the window is part of the key so
 *   switching the top-bar range selector refetches (and TanStack keeps a
 *   per-window cache entry, so flipping back is instant).
 *
 * Mirrors the `useAdminQueueCounts` pattern: typed to the centralised
 * `@carcheck/shared` `AdminMetricsDto` (api + admin share one source of
 * truth, no mirror drift), a plain `apiClient.get` with the request
 * `signal` threaded for abort-on-unmount.
 *
 * The endpoint is `@RequirePermissions('view_analytics')` server-side
 * (403 without) — the SPA additionally gates the tiles behind the same
 * permission so a non-analytics admin never fires this request.
 *
 * `refetchInterval` gives the dashboard's "LIVE" pill real cadence,
 * layered on the global `staleTime` / `refetchOnWindowFocus`.
 */

const METRICS_BASE = '/admin/metrics';

/** Stable query-key factory for the metrics read (window-scoped). */
export const adminMetricsKey = (window: AdminMetricsWindow): readonly [string, string, string] =>
  ['admin', 'metrics', window] as const;

/** Poll cadence for the dashboard "LIVE" feel (ms). Matches the 30s global staleTime. */
export const METRICS_REFETCH_MS = 30_000;

export const useAdminMetrics = (
  window: AdminMetricsWindow,
): UseQueryResult<AdminMetricsDto, Error> =>
  useQuery({
    queryKey: adminMetricsKey(window),
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminMetricsDto>(METRICS_BASE, {
        params: { window },
        signal,
      });
      return data;
    },
    refetchInterval: METRICS_REFETCH_MS,
  });
